import React, {Children, createContext, useContext, useEffect, useReducer} from 'react';
import HeaderAnswerTranslationItem from "../Data/HeaderAnswerTranslationItem";
import ImageAnswerTranslationItem from "../Data/ImageAnswerTranslationItem";
import NumericValueTranslationItem from "../Data/NumericValueTranslationItem";
import SummaryTranslationItem from "../Data/SummaryTranslationItem";
import TextTitleItem from "../Data/TextTitleItem";
import ZooPassFetch from "../Utilities/ZooPassFetch";
import {ZooPassOfferingItem} from "../Data/ZooContrib";
import { ZooMemberDetails } from '../Data/TnewMemberDetails';
import useMembership from '../Hooks/useMembership';
import useLogin from '../../MyAccount/Hooks/useLogin';

export type ZooPassState = {
    dictionary: Map<string, string>;
    headerAnswers: Map<string, HeaderAnswerTranslationItem>;
    imageAnswers: Map<string, ImageAnswerTranslationItem>;
    numericValues: Map<string, NumericValueTranslationItem>;
    summaryTranslations: Map<string, SummaryTranslationItem>;
    textTitles: Map<string, TextTitleItem>;
    zooPassMembership: Map<string, ZooPassOfferingItem>;
    loading: boolean;
    error: string | null;
    memberShipDetails?: ZooMemberDetails;
}

export type ZooPassAction =
    | { type: 'FETCH_INIT' }
    | { type: 'FETCH_SUCCESS'; 
    loaded: {
        dictionary: Map<string, string>;
        headerAnswers: Map<string, HeaderAnswerTranslationItem>;
        imageAnswers: Map<string, ImageAnswerTranslationItem>;
        numericValues: Map<string, NumericValueTranslationItem>;
        summaryTranslations: Map<string, SummaryTranslationItem>;
        zooPassMembership: Map<string, ZooPassOfferingItem>;
        textTitles: Map<string, TextTitleItem>;
    } }
    | { type: 'FETCH_FAILURE'; error: string | any }
    | { type: 'SET_MEMBER_DETAILS'; details?: ZooMemberDetails };

const initialState: ZooPassState = {
    dictionary: new Map<string, string>(),
    headerAnswers: new Map<string, HeaderAnswerTranslationItem>(),
    imageAnswers: new Map<string, ImageAnswerTranslationItem>(),
    numericValues: new Map<string, NumericValueTranslationItem>(),
    summaryTranslations: new Map<string, SummaryTranslationItem>(),
    zooPassMembership: new Map<string, ZooPassOfferingItem>(),
    textTitles: new Map<string, TextTitleItem>(),
    loading: true,
    error: null
}

function reducer(state: ZooPassState = initialState, action: ZooPassAction): ZooPassState {
    switch (action.type) {
        case 'FETCH_INIT':
            return {
                ...state,
                loading: true,
                error: null
            };
        case 'FETCH_SUCCESS':
            return {
                ...state,
                dictionary: action.loaded.dictionary,
                headerAnswers: action.loaded.headerAnswers,
                imageAnswers: action.loaded.imageAnswers,
                numericValues: action.loaded.numericValues,
                summaryTranslations: action.loaded.summaryTranslations,
                textTitles: action.loaded.textTitles,
                zooPassMembership: action.loaded.zooPassMembership,
                loading: false,
                error: null
            };
        case 'FETCH_FAILURE':
            return {
                ...state,
                loading: false,
                error: action.error
            };
        case 'SET_MEMBER_DETAILS':
            return {
                ...state,
                memberShipDetails: action.details
            };
        default:
            throw new Error(`Unknown action type: ${JSON.stringify(action)}`);
    }
}

export interface ZooPassAppContext extends ZooPassState {
    updateAppContext: React.Dispatch<ZooPassAction>;
}

export const ZooPassAppContext = React.createContext<ZooPassAppContext>({ ...initialState, updateAppContext: () => {} });

export const ZooPassContextProvider = ({children} : any) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const { getMemberInfo, verifySession } = useMembership();
    
    useEffect(() => {

        const fetchData = async () => {
            dispatch({type: 'FETCH_INIT'});
            
            try {
                const [dictionary, headerAnswers, imageAnswers, numericValues, summaryTranslations, textTitles, zooPassMembership] = await Promise.all([
                    // @ts-ignore
                    ZooPassFetch.fetchDictionary(window.baseApiUrl, window.apiCulture),
                    // @ts-ignore
                    ZooPassFetch.fetchHeaderAnswers(window.baseApiUrl, window.apiCulture),
                    // @ts-ignore
                    ZooPassFetch.fetchImageAnswers(window.baseApiUrl, window.apiCulture),
                    // @ts-ignore
                    ZooPassFetch.fetchNumericValues(window.baseApiUrl, window.apiCulture),
                    // @ts-ignore
                    ZooPassFetch.fetchSummaryTranslation(window.baseApiUrl, window.apiCulture),
                    // @ts-ignore
                    ZooPassFetch.fetchTitleText(window.baseApiUrl, window.apiCulture),
                    // @ts-ignore
                    ZooPassFetch.fetchZooPassOfferings(window.baseApiUrl, window.apiCulture)
                ]);

                dispatch({
                    type: 'FETCH_SUCCESS',
                    loaded: {
                        dictionary,
                        headerAnswers,
                        imageAnswers,
                        numericValues,
                        summaryTranslations,
                        zooPassMembership,
                        textTitles
                    }
                });
            } catch (e) {
                dispatch({
                    type: 'FETCH_FAILURE',
                    error: e
                })
            }
            
        };
        fetchData();

        verifySession().then(() => getMemberInfo()
                .then(memberDetails => {
                dispatch({
                    type: 'SET_MEMBER_DETAILS',
                    details: memberDetails
                });
            }).catch(error => {
                console.warn(error);
                console.info("Setting default member details");
                // only clear the membershipdetails if we had an error fetching them
                state.memberShipDetails && dispatch({
                    type: 'SET_MEMBER_DETAILS'
                });
            }));
    }, []);
    return <ZooPassAppContext.Provider value={{...state, updateAppContext: dispatch}}>
        {children}
    </ZooPassAppContext.Provider>
}

export const useZooPassAppContext = () => useContext(ZooPassAppContext);